<template>
  <div class="screen">

    <!-- Dark overlay — closes drawer on click -->
    <transition name="overlay-fade">
      <div
        v-if="menuOpen"
        class="menu-overlay"
        @click="toggleMenu"
        aria-hidden="true"
      ></div>
    </transition>

    <!-- Slide-in navigation drawer -->
    <transition name="drawer">
      <div class="menu-drawer" v-if="menuOpen">
        <!-- Logo row -->
        <div class="drawer-logo-row">
          <span class="drawer-logo-text">DIY Cardmaker</span>
        </div>
        <!-- Links — each gets full border, no overlap gaps -->
        <a class="drawer-link" href="?screen=HowToUseScreen" target="_blank">How to Use</a>
        <a class="drawer-link drawer-link--accent" href="https://roxanneg.design" target="_blank" rel="noopener">Roxanneg.design</a>
        <a class="drawer-link drawer-link--accent" href="https://boutell.dev" target="_blank" rel="noopener">Boutell.dev</a>
        <a class="drawer-link drawer-link--accent" href="https://roxanneg.design/#contact" target="_blank" rel="noopener">Submit a bug</a>
      </div>
    </transition>

    <!-- Nav bar — always on top via z-index -->
    <nav v-if="menu" class="main">
      <button
        class="menu-toggle"
        @click="toggleMenu"
        :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
        :aria-expanded="menuOpen"
      >
        <img v-if="menuOpen" src="../assets/icons/Icon-X.svg" class="menu-icon" alt="Close menu" />
        <img v-else src="../assets/icons/Icon-Menu.svg" class="menu-icon" alt="Open menu" />
      </button>
      <button @click="$emit('back')" class="back" v-if="back">
        <ArrowLeftIcon />
      </button>
      <span class="title">{{ title }}</span>
      <slot name="action"></slot>
    </nav>

    <div v-if="content" class="content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import ArrowLeftIcon from 'vue-material-design-icons/ArrowLeft.vue';

export default {
  components: {
    ArrowLeftIcon
  },
  data() {
    return {
      menuOpen: false
    }
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    }
  },
  props: {
    title: {
      type: String,
      required: true
    },
    back: {
      type: Boolean,
      required: false,
      default() { return false; }
    },
    content: {
      type: Boolean,
      required: false,
      default() { return true; }
    },
    menu: {
      type: Boolean,
      required: false,
      default() { return true; }
    },
  }
}
</script>

<style scoped>
/* ── Screen root ── */
.screen {
  position: relative;
  box-sizing: border-box;
  font-family: var(--font-family-balsamiq);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-lightest);
  background-image: url('~@/assets/images/Background-desktop.jpg');
  background-repeat: repeat;
  background-size: 50%;
  background-color: var(--surface-dark);
  margin: auto;
  display: flex;
  flex-direction: column;
  align-content: center;
  padding: 0;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
}

@media screen and (max-width: 1024px) {
  .screen { background-size: 40%; }
}
@media screen and (max-width: 480px) {
  .screen { background-size: 30%; }
}

/* ── Dark overlay — starts below nav bar ── */
.menu-overlay {
  position: fixed;
  top: 56px;  /* below nav bar */
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 8, 7, 0.4);
  z-index: 90;
  cursor: pointer;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}

/* ── Slide-in drawer — starts below nav bar ── */
.menu-drawer {
  position: fixed;
  top: 56px;
  left: 0;
  z-index: 100;
  width: 280px;
  height: calc(100% - 56px);
  background-color: var(--surface-dark);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow-y: auto;
  box-shadow: 4px 0 24px rgba(0, 8, 7, 0.5);
  padding-left: 24px;
  box-sizing: border-box;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.25s ease;
}
.drawer-enter,
.drawer-leave-to {
  transform: translateX(-100%);
}

/* Logo row */
.drawer-logo-row {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  border: 1px solid var(--text-bright);
  box-sizing: border-box;
  flex-shrink: 0;
}

.drawer-logo-text {
  font-family: var(--font-family-balsamiq);
  font-size: 24px;
  color: var(--text-bright);
  white-space: nowrap;
}

/*
  Nav links — horizontal separator lines only (no full rectangle).
  ::before renders a tall strip with the hand-drawn-line filter;
  only a 1px band is visible because of the gradient.
  This lets the filter act on real height while text stays unaffected.
*/
.drawer-link {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px 8px 16px;
  /* No CSS border — separator lines come from ::before */
  border: none;
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 24px;
  color: var(--text-bright) !important;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  white-space: nowrap;
  cursor: pointer;
  background-color: var(--surface-dark);
  overflow: visible;
}

.drawer-link:link,
.drawer-link:visited,
.drawer-link:hover,
.drawer-link:active {
  color: var(--text-bright) !important;
}

/* Top separator — uses hand-drawn-line filter for wavy effect */
.drawer-link::before {
  content: '';
  position: absolute;
  /* Make this strip tall enough for the filter displacement to be visible */
  top: -8px;
  left: 0;
  right: 0;
  height: 16px;
  /* Only the middle 1px appears as the line; rest is transparent */
  background: linear-gradient(
    to bottom,
    transparent calc(50% - 0.5px),
    var(--text-bright) calc(50% - 0.5px),
    var(--text-bright) calc(50% + 0.5px),
    transparent calc(50% + 0.5px)
  );
  filter: url(#hand-drawn-line);
  pointer-events: none;
  z-index: 1;
}

/* Bottom separator on last link */
.drawer-link:last-child::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 16px;
  background: linear-gradient(
    to bottom,
    transparent calc(50% - 0.5px),
    var(--text-bright) calc(50% - 0.5px),
    var(--text-bright) calc(50% + 0.5px),
    transparent calc(50% + 0.5px)
  );
  filter: url(#hand-drawn-line);
  pointer-events: none;
  z-index: 1;
}

/* Accent links: 2px aqua left accent bar + extra indent */
.drawer-link--accent {
  border-left: 2px solid var(--text-bright);
  padding-left: 14px;
}


/* ── Nav bar — must sit above overlay (z-index > 90) ── */
.main {
  position: relative;
  z-index: 110; /* higher than overlay (90) and drawer (100) */
  margin-top: 8px;
  margin-left: 16px;
  margin-right: 12px;
  font-size: 20px;
  display: flex;
  align-items: center;
}

/* ── Hamburger / X button — inherits z-index from .main ── */
.menu-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: var(--text-lightest);
  flex-shrink: 0;
}

/* ── Hamburger / X icon images ── */
.menu-icon {
  width: 32px;
  height: 32px;
  display: block;
}

/* ── Other nav elements ── */
.title {
  font-family: Cabin-Sketch;
  padding-left: 10px;
  font-weight: bold;
  font-size: 30px;
  flex-grow: 0;
  color: var(--text-lightest);
}

.back {
  color: var(--text-lightest);
  background-color: transparent;
  height: 48px;
  border: 0;
  padding: 0;
  margin: 0;
}

.back svg {
  height: 48px;
}

.content {
  margin-top: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.logo {
  height: 56px;
}

@media screen and (min-width: 411px) {
  .title { font-size: 36px; }
}
</style>